/**
 * InsightCallout.jsx — "What to notice" caption / highlight strip.
 *
 * Place under complex charts so the user does not need to inspect tooltips
 * to grasp the headline. Tones map to typical chart insight semantics.
 *
 * Variants:
 *   - tone="info"     (default, blue)
 *   - tone="positive" (emerald)
 *   - tone="negative" (rose)
 *   - tone="warning"  (amber)
 *   - tone="neutral"  (slate)
 *
 * Example:
 *   <InsightCallout
 *     label="What to notice"
 *     text="Operations is 18pp below the overall support baseline."
 *     tone="warning"
 *   />
 */
import { Sparkles, AlertTriangle, TrendingUp, TrendingDown, Info } from 'lucide-react';
import { cn } from '../../lib/utils';

const TONE = {
  info: {
    bg: 'bg-blue-50/60 dark:bg-blue-500/10',
    border: 'border-blue-200/70 dark:border-blue-500/30',
    label: 'text-blue-700 dark:text-blue-300',
    text: 'text-slate-700 dark:text-slate-200',
    icon: Sparkles,
    iconColor: 'text-blue-500 dark:text-blue-400',
  },
  positive: {
    bg: 'bg-positive-soft/60 ',
    border: 'border-positive-border/70 ',
    label: 'text-positive-soft-foreground ',
    text: 'text-slate-700 dark:text-slate-200',
    icon: TrendingUp,
    iconColor: 'text-positive ',
  },
  negative: {
    bg: 'bg-negative-soft/60 ',
    border: 'border-negative-border/70 ',
    label: 'text-negative-soft-foreground ',
    text: 'text-slate-700 dark:text-slate-200',
    icon: TrendingDown,
    iconColor: 'text-negative ',
  },
  warning: {
    bg: 'bg-warning-soft/60 ',
    border: 'border-warning-border/70 ',
    label: 'text-warning-soft-foreground ',
    text: 'text-slate-700 dark:text-slate-200',
    icon: AlertTriangle,
    iconColor: 'text-warning ',
  },
  neutral: {
    bg: 'bg-slate-50 dark:bg-slate-800/40',
    border: 'border-slate-200 dark:border-slate-700',
    label: 'text-slate-600 dark:text-slate-300',
    text: 'text-slate-700 dark:text-slate-200',
    icon: Info,
    iconColor: 'text-slate-400 dark:text-slate-500',
  },
};

export default function InsightCallout({
  label = 'What to notice',
  text,
  tone = 'info',
  className,
  testId = 'insight-callout',
}) {
  if (!text) return null;
  const c = TONE[tone] || TONE.info;
  const Icon = c.icon;

  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-lg border px-3 py-2',
        c.bg,
        c.border,
        className
      )}
      data-testid={testId}
    >
      <Icon className={cn('w-4 h-4 mt-0.5 flex-shrink-0', c.iconColor)} />
      <div className="flex-1 min-w-0">
        <p className={cn('text-[10px] font-semibold uppercase tracking-wider', c.label)}>
          {label}
        </p>
        <p className={cn('text-xs leading-relaxed mt-0.5', c.text)}>{text}</p>
      </div>
    </div>
  );
}
